import Jsona from 'jsona';
import { type ZodType } from 'zod';

// oxlint-disable-next-line no-restricted-imports
import { getCSRFToken } from 'lib/csrf';

type Simplify<T> = { [K in keyof T]: T[K] } & {};

export const dataFormatter = new Jsona();

const ACCEPT_V1_HEADER = 'application/json';
export const ACCEPT_V2_HEADER = 'application/vnd.user-interviews.v2';

export const headersAndCredentials = (accept = ACCEPT_V1_HEADER) => ({
  headers: {
    Accept: accept,
    'X-Requested-With': 'XMLHTTPRequest',
    'X-CSRF-Token': getCSRFToken() as string | undefined,
  },
  credentials: 'same-origin' as RequestCredentials,
});

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const deserializeJSONApiResponse = (response: any) =>
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  dataFormatter.deserialize(response) as any;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const deserializeJSONApiResponseWithMeta = (response: any) => {
  const data = dataFormatter.deserialize(response);

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  return { data: data as any, meta: response.meta as any };
};

export const parseJSONApiResponse =
  <T>(schema: ZodType<T>) =>
  (response: unknown) =>
    schema.parse(deserializeJSONApiResponse(response));

export const parseJSONApiResponseWithMeta =
  <T>(schema: ZodType<T>) =>
  (response: unknown): T =>
    schema.parse(deserializeJSONApiResponseWithMeta(response));

const sortAlphabeticalByKey = <T>(objectArray: T[], key: keyof T) =>
  objectArray.sort((a, b) =>
    (a[key] as string).localeCompare(b[key] as string),
  );

export const sortAlphabeticalByName = <T>(
  objectArray: Array<T & { name: string }>,
) => sortAlphabeticalByKey(objectArray, 'name');

export const sortAlphabeticalByTitle = <T>(
  objectArray: Array<T & { title: string }>,
) => sortAlphabeticalByKey(objectArray, 'title');

export type JsonApiResource = {
  id: string;
  type: string;
  attributes: Record<string, unknown>;
  meta?: Record<string, unknown>;
};

export type FlattenedResource<D extends JsonApiResource> = Simplify<
  Pick<D, 'id' | 'type'> &
    D['attributes'] &
    ('meta' extends keyof D ? Pick<D, 'meta'> : {})
>;

export function flattenResource<D extends JsonApiResource>(
  data: D,
): FlattenedResource<D> {
  return {
    id: data.id,
    type: data.type,
    ...data.attributes,
    ...(data.meta !== undefined && { meta: data.meta }),
  } as FlattenedResource<D>;
}
